import React, { useState } from "react";
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Button,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import BgTitle from "components/title/BgTitle";
import { useTranslation } from "react-i18next";
import NewsMobileView from "./NewsMobileView";
import { motion, AnimatePresence } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";

const News = ({ isXs = false }) => {
  const { t } = useTranslation();
  const newsItems = t("news", { returnObjects: true });
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1); // 1 for right, -1 for left

  const itemsPerSlide = 3;
  const totalSlides = Math.ceil(newsItems.length / itemsPerSlide);
  const needsSlider = newsItems.length > 3;

  const goToNextSlide = () => {
    setDirection(1);
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const goToPrevSlide = () => {
    setDirection(-1);
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const getCurrentItems = () => {
    const startIndex = currentSlide * itemsPerSlide;
    return newsItems.slice(startIndex, startIndex + itemsPerSlide);
  };

  if (isXs) return <NewsMobileView />;

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Box sx={{ mb: 5, textAlign: "center" }}>
        <BgTitle text={t("news_headline")} />
      </Box>

      {needsSlider && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: "medium", color: "#0c4d96" }}
            >
              {currentSlide + 1} / {totalSlides}
            </Typography>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                onClick={goToPrevSlide}
                variant="outlined"
                sx={{
                  minWidth: "36px",
                  width: "36px",
                  height: "36px",
                  p: 0,
                  borderRadius: "8px",
                  borderColor: "#0c4d96",
                  color: "#0c4d96",
                  "&:hover": {
                    borderColor: "#0c4d96",
                    backgroundColor: "rgba(12, 77, 150, 0.04)",
                  },
                }}
              >
                <ArrowBackIosNewIcon
                  fontSize="small"
                  sx={{ fontSize: "18px" }}
                />
              </Button>
              <Button
                onClick={goToNextSlide}
                variant="outlined"
                sx={{
                  minWidth: "36px",
                  width: "36px",
                  height: "36px",
                  p: 0,
                  borderRadius: "8px",
                  borderColor: "#0c4d96",
                  color: "#0c4d96",
                  "&:hover": {
                    borderColor: "#0c4d96",
                    backgroundColor: "rgba(12, 77, 150, 0.04)",
                  },
                }}
              >
                <ArrowForwardIosIcon
                  fontSize="small"
                  sx={{ fontSize: "18px" }}
                />
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <AnimatePresence initial={false} custom={direction} mode="wait">
        <motion.div
          key={currentSlide}
          custom={direction}
          initial={{ opacity: 0, x: direction * 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -direction * 50 }}
          transition={{ type: "tween", duration: 0.4, ease: "easeInOut" }}
        >
          <Grid container spacing={4}>
            {(needsSlider ? getCurrentItems() : newsItems).map(
              (item, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card
                    elevation={3}
                    sx={{
                      height: "100%",
                      borderRadius: 2,
                      transition: "all 0.25s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 6px 20px rgba(12, 77, 150, 0.08)",
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        height: "100%",
                        p: 3,
                        "&:last-child": { pb: 3 },
                      }}
                    >
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 650,
                            mb: 1.5,
                            display: "block",
                            fontSize: "0.75rem",
                            letterSpacing: "0.5px",
                            textTransform: "uppercase",
                            color: item?.important ? "red" : "#0c4d96",
                          }}
                        >
                          {item.date}
                        </Typography>
                        <Typography
                          variant="h6"
                          component="div"
                          sx={{
                            fontWeight: 600,
                            fontSize: "1rem",
                            lineHeight: 1.4,
                            color: "#000000",
                            mb: 1,
                          }}
                        >
                          {item.title}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          mt: 2,
                        }}
                      >
                        {item.link &&
                          (item.link.startsWith("/") ? (
                            <Button
                              component={RouterLink}
                              to={item.link}
                              variant="text"
                              size="small"
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.8125rem",
                                p: 0,
                                color: "#0c4d96",
                                "&:hover": {
                                  backgroundColor: "transparent",
                                  opacity: 0.8,
                                },
                              }}
                            >
                              {t("news_read_more")}
                            </Button>
                          ) : (
                            <Button
                              href={item.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              endIcon={<OpenInNewIcon fontSize="small" />}
                              variant="text"
                              size="small"
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.8125rem",
                                p: 0,
                                color: "#0c4d96",
                                "&:hover": {
                                  backgroundColor: "transparent",
                                  opacity: 0.8,
                                },
                              }}
                            >
                              {t("news_read_more")}
                            </Button>
                          ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )
            )}
          </Grid>
        </motion.div>
      </AnimatePresence>

      {needsSlider && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4, gap: 1 }}>
          {Array.from({ length: totalSlides }).map((_, index) => (
            <Box
              key={index}
              onClick={() => {
                setDirection(index > currentSlide ? 1 : -1);
                setCurrentSlide(index);
              }}
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor:
                  index === currentSlide
                    ? "#0c4d96"
                    : "rgba(12, 77, 150, 0.15)",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor:
                    index === currentSlide
                      ? "#0c4d96"
                      : "rgba(12, 77, 150, 0.3)",
                },
              }}
            />
          ))}
        </Box>
      )}
    </Container>
  );
};

export default News;
